import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const TwitterContext = createContext();

export const useTwitter = () => useContext(TwitterContext);

const TWITTER_CLIENT_ID = import.meta.env.VITE_TWITTER_CLIENT_ID;
const TWITTER_REDIRECT_URI = import.meta.env.VITE_TWITTER_REDIRECT_URI;
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

export const TwitterProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    } else {
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/twitter/accounts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load Twitter accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectTwitter = useCallback(async () => {
    try {
      // Step 1: Get PKCE challenge from backend
      const resp = await fetch(`${API_BASE_URL}/twitter/oauth/start`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!resp.ok) {
        throw new Error('Failed to start Twitter OAuth');
      }

      const { auth_url_template, state } = await resp.json();

      // Store state for verification
      sessionStorage.setItem('twitter_oauth_state', state);

      // Build the auth URL with redirect_uri
      const authUrl = auth_url_template.replace('{redirect_uri}', encodeURIComponent(TWITTER_REDIRECT_URI));
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to start Twitter OAuth:', err);
      setError('Failed to start Twitter connection');
    }
  }, []);

  const handleCallback = useCallback(async (code, state) => {
    const savedState = sessionStorage.getItem('twitter_oauth_state');
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/twitter/oauth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          state,
          redirect_uri: TWITTER_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect Twitter account');
      }

      const data = await response.json();
      sessionStorage.removeItem('twitter_oauth_state');

      // Dedupe by id: re-connecting an account already linked to this org returns
      // the SAME account (already_connected=true), not a new one — update in place.
      setAccounts(prev => {
        const exists = prev.some(a => a.id === data.account.id);
        return exists
          ? prev.map(a => (a.id === data.account.id ? data.account : a))
          : [...prev, data.account];
      });
      return { account: data.account, alreadyConnected: !!data.already_connected };
    } catch (err) {
      sessionStorage.removeItem('twitter_oauth_state');
      throw err;
    }
  }, []);

  const disconnectAccount = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/twitter/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        throw new Error('Failed to disconnect account');
      }
    } catch (err) {
      console.error('Failed to disconnect Twitter account:', err);
      setError(err.message);
    }
  }, []);

  const refreshAccountStats = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/twitter/accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
        );
        return data.account;
      } else {
        throw new Error('Failed to refresh stats');
      }
    } catch (err) {
      console.error('Failed to refresh Twitter stats:', err);
      setError(err.message);
    }
  }, []);

  const fetchTweets = useCallback(async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/twitter/accounts/${accountId}/tweets`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch tweets');
    }
    return response.json();
  }, []);

  const postTweet = useCallback(async (accountId, text, replyToId) => {
    const body = { text };
    if (replyToId) body.reply_to_id = replyToId;
    const response = await fetch(`${API_BASE_URL}/twitter/accounts/${accountId}/tweet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to post tweet');
    }
    return response.json();
  }, []);

  const deleteTweet = useCallback(async (accountId, tweetId) => {
    const response = await fetch(`${API_BASE_URL}/twitter/accounts/${accountId}/tweets/${tweetId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete tweet');
    }
    return response.json();
  }, []);

  const getAccountById = useCallback((accountId) => {
    return accounts.find(a => a.id === accountId);
  }, [accounts]);

  return (
    <TwitterContext.Provider
      value={{
        accounts,
        loading,
        error,
        connectTwitter,
        handleCallback,
        disconnectAccount,
        refreshAccountStats,
        loadConnectedAccounts,
        fetchTweets,
        postTweet,
        deleteTweet,
        getAccountById,
      }}
    >
      {children}
    </TwitterContext.Provider>
  );
};

export default TwitterContext;
